import { create } from 'zustand';

import { closeOtherDrawers, registerDrawerCloser } from './_drawer-mutex';

import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import type { PlanStrategy } from '@/lib/ai-agent/schemas/plan-strategy';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

export interface StrategyDialogPayload {
  projectId: string;
  projectName: string;
  strategy: PlanStrategy;
  updatedAt?: string;
}

interface StrategyDialogState {
  isOpen: boolean;
  payload: StrategyDialogPayload | null;
  onClose?: () => void;
  open: (args: {
    strategy: StrategyDialogPayload;
    breadcrumbs?: InspectorBreadcrumbItem[];
    onClose?: () => void;
  }) => void;
  close: () => void;
}

/**
 * Strategy dialog payload carrier. Mirrors useDocumentDialogStore — calling
 * `open()` here pushes a strategy subject onto the Inspector store so the
 * GlobalInspectorMount renders StrategyBody, which wraps PlanStrategyCanvas.
 *
 * Used from /library when a user clicks a strategy artifact row.
 */
export const useStrategyDialogStore = create<StrategyDialogState>((set, get) => ({
  isOpen: false,
  payload: null,
  onClose: undefined,
  open: ({ strategy, breadcrumbs, onClose }) => {
    closeOtherDrawers('strategy');
    set({ isOpen: true, payload: strategy, onClose });
    useInspector
      .getState()
      .openSubject(
        { kind: 'strategy', projectId: strategy.projectId },
        { presentation: 'workspace', breadcrumbs },
      );
  },
  close: () => {
    const { onClose } = get();
    set({ isOpen: false, payload: null, onClose: undefined });
    onClose?.();
    const inspector = useInspector.getState();
    const top = inspector.stack[inspector.stack.length - 1];
    if (top?.kind === 'strategy') inspector.close();
  },
}));

registerDrawerCloser('strategy', () => useStrategyDialogStore.getState().close());

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (
    window as unknown as { __strategyDialogStore: typeof useStrategyDialogStore }
  ).__strategyDialogStore = useStrategyDialogStore;
}

registerWorkspaceReset('strategy-dialog-store', () => {
  useStrategyDialogStore.setState({ isOpen: false, payload: null, onClose: undefined });
  const inspector = useInspector.getState();
  const top = inspector.stack[inspector.stack.length - 1];
  if (top?.kind === 'strategy') inspector.close();
});
